/**
 * WorkBestaetigung — Public Monats-Bestaetigung via Token-Link (#892).
 *
 * DN oeffnet Link aus SMS, sieht Monats-Zusammenfassung (Soll/Ist/Delta/ZA-Saldo)
 * und kann entweder bestaetigen oder ablehnen (mit Grund).
 *
 * #1807 — die Detailpruefung laeuft ueber den PDF-Monatsnachweis (dasselbe Dokument wie
 * im Portal/HR), geoeffnet als Overlay INNERHALB der Seite: kein Redirect, kein Unmount,
 * damit Token und Scrollposition erhalten bleiben und "Bestaetigen"/"Ablehnen" nach dem
 * Schliessen sofort wieder erreichbar sind. Ersetzt die Tagesliste aus #1787.
 *
 * Touch-friendly Design wie WorkLogin:
 * - Large fonts (18px+), Large touch targets (56px+)
 * - max-w-[340px] container
 * - Simple language, minimal text
 *
 * @page /work/bestaetigung/:token (public, ohne Login)
 * @endpoints GET/POST /api/public/bestaetigung/:token, GET /api/public/bestaetigung/:token/pdf
 * @since v2.384.0
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { translate } from '../i18n';
import { useTheme } from '../context/ThemeContext';

// #1732: Magic-Link-Seite ohne Session — Sprache NICHT aus DEFAULT_LANGUAGE ('en'),
// sondern explizit gewählte Browser-Sprache, sonst hart DE (MA haben keine DB-Präferenz).
function resolveLanguage() {
  try {
    const stored = localStorage.getItem('weship-language');
    if (stored === 'en' || stored === 'de') return stored;
  } catch { /* storage blocked → DE */ }
  return 'de';
}

function fmtH(val, fallback = '—') {
  if (val === null || val === undefined) return fallback;
  const n = Number(val);
  if (!Number.isFinite(n)) return fallback;
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)} h`;
}

function fmtDate(val) {
  if (!val) return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

export default function WorkBestaetigung() {
  const { token } = useParams();
  const [language] = useState(resolveLanguage);
  const t = useCallback((key, defaultOrParams, params) => translate(language, key, defaultOrParams, params), [language]);
  const { theme } = useTheme();

  const [state, setState] = useState('loading'); // loading | ready | done | rejected | expired | error | notfound
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [grund, setGrund] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPdf, setShowPdf] = useState(false); // #1807 — Overlay, bewusst zu, damit die Bestaetigung praesent bleibt

  const apiBase = import.meta.env.VITE_API_URL || '';
  const pdfUrl = `${apiBase}/api/public/bestaetigung/${token}/pdf`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`${apiBase}/api/public/bestaetigung/${token}`);
        if (res.status === 404) {
          if (!cancelled) setState('notfound');
          return;
        }
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok) { setState('error'); setErrorMsg(json.error || t('zeitkontoBestaetigung.genericError', 'Fehler beim Laden.')); return; }
        setData(json.zeitkonto);
        if (json.status === 'hr_freigegeben') setState('ready');
        else if (json.status === 'dn_bestaetigt') setState('done');
        else if (json.status === 'dn_abgelehnt') setState('rejected');
        else if (json.status === 'frist_abgelaufen') setState('expired');
        else setState('error');
      } catch (err) {
        if (!cancelled) { setState('error'); setErrorMsg(err.message); }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token, apiBase]);

  const submit = async (aktion) => {
    if (aktion === 'ablehnen' && grund.trim().length < 3) {
      setErrorMsg(t('zeitkontoBestaetigung.grundZuKurz', 'Bitte gib einen Grund an (mindestens 3 Zeichen).'));
      return;
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await fetch(`${apiBase}/api/public/bestaetigung/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktion, grund: aktion === 'ablehnen' ? grund.trim() : undefined }),
      });
      const json = await res.json();
      if (!res.ok) {
        setErrorMsg(json.error || t('zeitkontoBestaetigung.genericError', 'Fehler beim Absenden.'));
        setSubmitting(false);
        return;
      }
      if (aktion === 'bestaetigen') setState('done');
      else setState('rejected');
    } catch (err) {
      setErrorMsg(err.message || t('zeitkontoBestaetigung.genericError', 'Fehler beim Absenden.'));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--bg-page,#F8F9FC)] flex flex-col items-center justify-center px-6 py-8">
      <div className="w-full max-w-[340px]">
        <div className="flex flex-col items-center mb-6">
          <img
            src={theme === 'dark' ? '/weship-logo-light.png' : '/weship-logo.png'}
            alt="WeShip"
            className="h-12 mb-4"
          />
          <h1 className="text-xl font-bold text-[var(--text-primary,#1A1A2E)] text-center">
            {t('zeitkontoBestaetigung.title', 'Arbeitszeit-Konto')}
          </h1>
          {data && (
            <p className="text-base text-[var(--text-tertiary,#6B7280)] mt-1">
              {data.monat_label}
            </p>
          )}
        </div>

        {state === 'loading' && (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-8 w-8 border-3 border-[#338edc] border-t-transparent rounded-full" />
          </div>
        )}

        {state === 'notfound' && (
          <Card>
            <div className="text-center py-4">
              <div className="text-5xl mb-3">🔒</div>
              <p className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">
                {t('zeitkontoBestaetigung.notFoundTitle', 'Link ungueltig')}
              </p>
              <p className="text-base text-[var(--text-secondary,#4B5563)]">
                {t('zeitkontoBestaetigung.notFoundBody', 'Der Link ist abgelaufen oder ungueltig. Bitte wende dich an das HR-Team.')}
              </p>
            </div>
          </Card>
        )}

        {state === 'error' && (
          <Card>
            <div className="text-center py-4">
              <div className="text-5xl mb-3">⚠️</div>
              <p className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">
                {t('zeitkontoBestaetigung.errorTitle', 'Fehler')}
              </p>
              <p className="text-base text-[var(--text-secondary,#4B5563)]">
                {errorMsg || t('zeitkontoBestaetigung.genericError', 'Fehler beim Laden.')}
              </p>
            </div>
          </Card>
        )}

        {state === 'done' && data && (
          <>
            <Card>
              <div className="text-center py-4">
                <div className="text-5xl mb-3">✅</div>
                <p className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">
                  {t('zeitkontoBestaetigung.doneTitle', 'Danke, bestaetigt!')}
                </p>
                <p className="text-base text-[var(--text-secondary,#4B5563)]">
                  {t('zeitkontoBestaetigung.doneBody', 'Dein Arbeitszeit-Konto ist bestaetigt.')}
                </p>
                {data.bestaetigt_am && (
                  <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-3">
                    {fmtDate(data.bestaetigt_am)}
                  </p>
                )}
              </div>
              <Summary data={data} t={t} />
            </Card>
            {/* #1807 — nach der Bestaetigung bleibt der Nachweis abrufbar (Nachvollziehbarkeit), nur ohne Aktion. */}
            <MonatsnachweisButton t={t} onOpen={() => setShowPdf(true)} />
          </>
        )}

        {state === 'rejected' && data && (
          <Card>
            <div className="text-center py-4">
              <div className="text-5xl mb-3">📬</div>
              <p className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">
                {t('zeitkontoBestaetigung.rejectedTitle', 'Rueckmeldung erhalten')}
              </p>
              <p className="text-base text-[var(--text-secondary,#4B5563)]">
                {t('zeitkontoBestaetigung.rejectedBody', 'Deine Rueckmeldung wurde an HR weitergeleitet. Du wirst kontaktiert.')}
              </p>
              {data.abgelehnt_grund && (
                <div className="mt-4 bg-[var(--bg-inset,#F8F9FC)] rounded-xl p-3 text-left">
                  <p className="text-sm text-[var(--text-tertiary,#6B7280)] mb-1">
                    {t('zeitkontoBestaetigung.deinGrund', 'Dein Grund:')}
                  </p>
                  <p className="text-base text-[var(--text-primary,#1A1A2E)]">{data.abgelehnt_grund}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {state === 'expired' && (
          <Card>
            <div className="text-center py-4">
              <div className="text-5xl mb-3">⏰</div>
              <p className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">
                {t('zeitkontoBestaetigung.expiredTitle', 'Frist abgelaufen')}
              </p>
              <p className="text-base text-[var(--text-secondary,#4B5563)]">
                {t('zeitkontoBestaetigung.expiredBody', 'Die Frist fuer diese Bestaetigung ist abgelaufen. Bitte wende dich an HR.')}
              </p>
            </div>
          </Card>
        )}

        {state === 'ready' && data && !showRejectForm && (
          <>
            <Card>
              <Summary data={data} t={t} />
              {data.frist_ende && (
                <p className="text-sm text-center text-[var(--text-tertiary,#6B7280)] mt-4">
                  {t('zeitkontoBestaetigung.fristHinweis', 'Frist fuer deine Antwort')}: {fmtDate(data.frist_ende)}
                </p>
              )}
            </Card>

            {errorMsg && (
              <p className="text-center text-base font-semibold text-red-500 mt-4">{errorMsg}</p>
            )}

            <div className="space-y-3 mt-5">
              <button
                type="button"
                onClick={() => submit('bestaetigen')}
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/40 text-white font-bold text-lg rounded-2xl transition-colors"
                style={{ height: 64 }}
              >
                {submitting ? t('zeitkontoBestaetigung.submitting', 'Wird gesendet...') : t('zeitkontoBestaetigung.bestaetigen', 'Bestaetigen')}
              </button>
              <button
                type="button"
                onClick={() => { setShowRejectForm(true); setErrorMsg(''); }}
                disabled={submitting}
                className="w-full bg-[var(--bg-card,#fff)] border border-red-300 text-red-600 hover:bg-red-50 font-bold text-lg rounded-2xl transition-colors"
                style={{ height: 56 }}
              >
                {t('zeitkontoBestaetigung.ablehnen', 'Ablehnen / Korrektur noetig')}
              </button>
            </div>

            {/* #1807 — Detailpruefung unterhalb der Aktionen: der Bestaetigen-Button bleibt
                dadurch immer oberhalb, das PDF oeffnet als Overlay ueber der Seite. */}
            <MonatsnachweisButton t={t} onOpen={() => setShowPdf(true)} />
          </>
        )}

        {state === 'ready' && data && showRejectForm && (
          <>
            <Card>
              <p className="text-base font-semibold text-[var(--text-primary,#1A1A2E)] mb-2">
                {t('zeitkontoBestaetigung.ablehnenTitle', 'Was passt nicht?')}
              </p>
              <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-3">
                {t('zeitkontoBestaetigung.ablehnenHint', 'Bitte beschreibe kurz, was korrigiert werden muss. HR meldet sich bei dir.')}
              </p>
              <textarea
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                maxLength={2000}
                rows={5}
                placeholder={t('zeitkontoBestaetigung.grundPlaceholder', 'z.B. 15.04. fehlt — war da im Dienst...')}
                className="w-full bg-[var(--bg-input,#F8F9FC)] border border-[var(--border-input,#E5E7EB)] rounded-xl px-4 py-3 text-base text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-muted,#9CA3AF)] focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20 resize-none"
              />
              <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-1 text-right">{grund.length} / 2000</p>
            </Card>

            {errorMsg && (
              <p className="text-center text-base font-semibold text-red-500 mt-4">{errorMsg}</p>
            )}

            <div className="space-y-3 mt-5">
              <button
                type="button"
                onClick={() => submit('ablehnen')}
                disabled={submitting || grund.trim().length < 3}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/40 text-white font-bold text-lg rounded-2xl transition-colors"
                style={{ height: 64 }}
              >
                {submitting ? t('zeitkontoBestaetigung.submitting', 'Wird gesendet...') : t('zeitkontoBestaetigung.ablehnenSend', 'Ablehnung absenden')}
              </button>
              <button
                type="button"
                onClick={() => { setShowRejectForm(false); setGrund(''); setErrorMsg(''); }}
                disabled={submitting}
                className="w-full bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] font-semibold text-base rounded-2xl"
                style={{ height: 52 }}
              >
                {t('zeitkontoBestaetigung.abbrechen', 'Zurueck')}
              </button>
            </div>
          </>
        )}
      </div>

      {showPdf && (
        <PdfOverlay
          t={t}
          pdfUrl={pdfUrl}
          monatLabel={data?.monat_label}
          onClose={() => setShowPdf(false)}
        />
      )}
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl p-5">
      {children}
    </div>
  );
}

/**
 * #1807 — Einstieg in den PDF-Monatsnachweis. Bewusst unterhalb der Aktionen, damit
 * "Bestaetigen"/"Ablehnen" immer oberhalb und ohne Scrollen erreichbar bleiben.
 */
function MonatsnachweisButton({ t, onOpen }) {
  return (
    <div className="mt-5">
      <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-center justify-center gap-2 bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] font-semibold text-base rounded-2xl"
        style={{ height: 56 }}
      >
        <span aria-hidden="true">📄</span>
        {t('zeitkontoBestaetigung.pdfShow', 'Monatsnachweis ansehen (PDF)')}
      </button>
    </div>
  );
}

/**
 * #1807 — Formatfuellendes Overlay INNERHALB der Seite: kein Redirect, kein Unmount,
 * damit Token und Scrollposition erhalten bleiben und die Aktionen nach dem Schliessen
 * sofort wieder klickbar sind.
 *
 * Der Hintergrund-Scroll wird ueber `body.style.overflow` gesperrt — bewusst NICHT ueber
 * den `position: fixed`-Trick, der auf iOS die Scrollposition auf 0 zurueckwirft.
 */
function PdfOverlay({ t, pdfUrl, monatLabel, onClose }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[var(--bg-page,#F8F9FC)]"
      role="dialog"
      aria-modal="true"
      aria-label={t('zeitkontoBestaetigung.pdfTitle', 'Zeitkonto — Monatsnachweis')}
    >
      <div className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-card,#fff)] border-b border-[var(--border-card,#E5E7EB)] shrink-0">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-[var(--text-primary,#1A1A2E)] truncate">
            {t('zeitkontoBestaetigung.pdfTitle', 'Zeitkonto — Monatsnachweis')}
          </p>
          {monatLabel && (
            <p className="text-sm text-[var(--text-tertiary,#6B7280)] truncate">{monatLabel}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('zeitkontoBestaetigung.pdfClose', 'Schliessen')}
          className="shrink-0 flex items-center justify-center rounded-xl bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-2xl leading-none text-[var(--text-secondary,#4B5563)]"
          style={{ width: 48, height: 48 }}
        >
          ×
        </button>
      </div>

      <iframe
        src={pdfUrl}
        title={t('zeitkontoBestaetigung.pdfTitle', 'Zeitkonto — Monatsnachweis')}
        className="flex-1 w-full min-h-0"
        style={{ border: 0 }}
      />

      {/* Fallback fuer Browser ohne Inline-PDF — Nebenweg, nicht der Hauptweg. */}
      <div className="flex items-center justify-center gap-4 px-4 py-3 bg-[var(--bg-card,#fff)] border-t border-[var(--border-card,#E5E7EB)] shrink-0">
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[var(--text-tertiary,#6B7280)] underline"
        >
          {t('zeitkontoBestaetigung.pdfOpenTab', 'In neuem Tab oeffnen')}
        </a>
        <a
          href={`${pdfUrl}?download=1`}
          className="text-sm text-[var(--text-tertiary,#6B7280)] underline"
        >
          {t('zeitkontoBestaetigung.pdfDownload', 'Herunterladen')}
        </a>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="shrink-0 mx-4 mb-4 mt-1 bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] font-semibold text-base rounded-2xl"
        style={{ height: 56 }}
      >
        {t('zeitkontoBestaetigung.pdfClose', 'Schliessen')}
      </button>
    </div>
  );
}

function Summary({ data, t }) {
  const rows = [
    { label: t('zeitkontoBestaetigung.sollStunden', 'Soll'), val: data.soll_stunden, positive: false },
    { label: t('zeitkontoBestaetigung.istStunden', 'Ist'), val: data.ist_stunden, positive: false },
    { label: t('zeitkontoBestaetigung.delta', 'Differenz'), val: data.delta_stunden, positive: true },
  ];
  return (
    <div className="space-y-2 mt-3">
      {rows.map(r => (
        <div key={r.label} className="flex items-center justify-between py-2 border-b border-[var(--border-card,#E5E7EB)] last:border-b-0">
          <span className="text-base text-[var(--text-secondary,#4B5563)]">{r.label}</span>
          <span className={`text-lg font-bold font-mono ${
            r.positive && Number(r.val) < 0 ? 'text-red-600' :
            r.positive && Number(r.val) > 0 ? 'text-emerald-600' :
            'text-[var(--text-primary,#1A1A2E)]'
          }`}>
            {fmtH(r.val)}
          </span>
        </div>
      ))}
      {data.za_saldo != null && (
        <div className="flex items-center justify-between py-2 pt-3 mt-2 border-t-2 border-[var(--border-card,#E5E7EB)]">
          <span className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">
            {t('zeitkontoBestaetigung.zaSaldo', 'ZA-Saldo')}
          </span>
          <span className={`text-xl font-bold font-mono ${
            Number(data.za_saldo) < 0 ? 'text-red-600' : 'text-emerald-600'
          }`}>
            {fmtH(data.za_saldo)}
          </span>
        </div>
      )}
    </div>
  );
}
